import { Cookies } from 'quasar'
import { COOKIES_TYPE } from 'src/boot/constants'
import { consumerRoot } from 'src/config'
import Model from 'src/models/model'

export function deletePrefix (str, prefix) {
  if (prefix && _.startsWith(str, prefix)) {
    return str.slice(prefix.length)
  }
  return str
}

export function deleteSuffix (str, suffix) {
  if (suffix && _.endsWith(str, suffix)) {
    return str.slice(0, -suffix.length)
  }
  return str
}

/**
 * Returns the path prepended by the `consumerRoot` unless it is
 * already present. Always returns a path starting with '/' and
 * never ending with '/'.
 */
export function prependWithConsumerRoot (path) {
  if (consumerRoot === '/') {
    path = _.trim(path, '/')
    return `/${path}`
  }

  path = deletePrefix(path, consumerRoot)
  path = _.trim(path, '/')
  // Assumes consumerRoot ends with '/'
  return `${consumerRoot}${path}`
}

export function createConsumerSitePathRegexp (siteRootPath) {
  siteRootPath = _.trim(siteRootPath, '/')
  const consumerSiteRootPath = prependWithConsumerRoot(siteRootPath)
  let pattern
  if (consumerSiteRootPath === '/') {
    pattern = '^/(.*)?$'
  } else {
    pattern = `^${_.escapeRegExp(consumerSiteRootPath)}(/.*)?/?$`
  }
  return new RegExp(pattern)
}

// Remove any wrong, non-root path prefix. Also remove non-'/'
// consumerRoot as that will be added back later if required.
export function removeMismatchedSiteRootPath (path) {
  const currentSite = Model.configuration.currentSite
  const sharedHostnameSiteIdsByPath = Model.configuration.currentSite.sharedHostnameSiteIdsByPath
  let unprefixedPath = path

  _.find(sharedHostnameSiteIdsByPath, (siteId, sitePath) => {
    // All paths start with '/' so no need to check for it.
    // We want the currentSite's path, so skip currentSite.
    if (sitePath !== '/' && siteId !== currentSite.id) {
      const consumerSiteRoot = prependWithConsumerRoot(sitePath)
      if (path === consumerSiteRoot) {
        unprefixedPath = '/'
        return true
      } else if (_.startsWith(path, `${consumerSiteRoot}/`)) {
        unprefixedPath = deletePrefix(path, consumerSiteRoot)
        return true
      }
    }

    return false
  })

  return unprefixedPath
}

export async function switchSite (toSiteId) {
  const previousSiteId = Model.configuration.currentSite.id
  if (Model.context.currentUser) {
    Model.context.currentUser.siteId = toSiteId
  }
  Model.context.clientReloadProgress = 0.01

  // These headers are set by Checkout route guards and will cause the API
  // to respond with the a site we might be switching away from.
  Model.app.$clearGlobalHeaders('showing-id', 'activity-id')
  const order = Model.app.config.globalProperties.$order
  if (order) {
    try {
      // We don't support multi-site orders
      order.abandon()
      order.reset()
    } catch (e) {
      console.error('Failed to abandon order', e)
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to abandon order', e, { previousSiteId, toSiteId })
      }
    }
  }

  try {
    // Client config cache versions to force a cache miss during config validate().
    Model.configuration.version = null
    await Model.app.config.globalProperties.$handleAuthChange(Model.configuration.currentCircuit.id, toSiteId, true)
    Model.context.clientReloadProgress = 0.4
    await Model.context.changeCircuitOrSite({ newLastVisitedSiteId: toSiteId })
    Model.context.clientReloadProgress = 0.6
  } catch (e) {
    console.error(e)
    if (typeof Rollbar !== 'undefined') {
      // eslint-disable-next-line no-undef
      Rollbar.error('Site switch failed', e, { previousSiteId, toSiteId })
    }
  }
}

/**
 * Stores the last visited site ID as a string in a cross-subdomain cookie.
 * @param {number} siteId
 */
export function storeLastVisitedSiteId (siteId) {
  siteId = parseInt(siteId)
  if (!_.isFinite(siteId)) {
    return null
  }

  // The Cookies plugin sets number values as strings anyway.
  const cookieValue = String(siteId)
  Cookies.set(COOKIES_TYPE.lastVisitedSiteId, cookieValue, getLastVisitedSiteIdCookieOptions())
  return cookieValue
}

/** @returns {number|null} */
export function getLastVisitedSiteIdFromCookie () {
  // TODO INDY-7055: Remove this after a couple of weeks on production.
  migrateDeprecatedSiteSelectorItemSiteId()

  const maybeSiteId = parseInt(Cookies.get(COOKIES_TYPE.lastVisitedSiteId, getLastVisitedSiteIdCookieOptions()))
  return _.isFinite(maybeSiteId) ? maybeSiteId : null
}

/**
 * When setting a cookie with certain options, they can only be read or removed
 * when accessed using the same options as when set.
 */
function getLastVisitedSiteIdCookieOptions () {
  const cookieOptions = { expires: 365, sameSite: 'Lax', path: '/' }
  if (!_.isEmpty(Model.configuration?.currentSite?.crossSubdomainCookieDomain)) {
    cookieOptions.domain = Model.configuration.currentSite.crossSubdomainCookieDomain
  }
  return cookieOptions
}

/**
 * @typedef {Object} DeprecatedSiteSelectorItemCookie
 * @property {number} siteId
 * @property {string} [externalUrl] - Optional external URL for cross-domain site URLs or custom links.
 */
const DEPRECATED_SITE_SELECTOR_ITEM_COOKIE = 'siteSelectorItemCookie'
function migrateDeprecatedSiteSelectorItemSiteId () {
  /** @type {DeprecatedSiteSelectorItemCookie|null} */
  const cookieValue = Cookies.get(DEPRECATED_SITE_SELECTOR_ITEM_COOKIE)
  if (!_.isEmpty(cookieValue)) {
    const siteId = parseInt(cookieValue.siteId)
    if (_.isFinite(siteId)) {
      storeLastVisitedSiteId(siteId)
    }
    Cookies.remove(DEPRECATED_SITE_SELECTOR_ITEM_COOKIE)
  }
}
